/**
 * /attendance/timesheets — HR view of all timesheet periods + the recompute
 * (aggregate) trigger (ADR-0014 Phase 4 PR-3). Mirrors leaveAdmin's HR-gated
 * page (full-page restricted guard + table). HR-gated on the API too.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import { useAuth } from '../auth/AuthContext.js';
import { aggregateTimesheetPeriods, listTimesheetPeriods } from '../api/attendance.js';
import { listEmployees } from '../api/employees.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate, formatNumber } from '../lib/format.js';

export const attendanceTimesheetsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/attendance/timesheets',
  component: AttendanceTimesheetsPage,
});

function AttendanceTimesheetsPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';

  if (!isHr) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">Restricted to HR / admin / owner roles.</Alert>
      </section>
    );
  }

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Timesheets</h1>
        <p className="text-sm text-gray-400">Aggregated hours per pay period. Recompute a period after importing or correcting punches.</p>
      </header>
      <RecomputePanel />
      <PeriodsTable />
    </section>
  );
}

const RecomputeSchema = z.object({
  periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
});
type RecomputeValues = z.infer<typeof RecomputeSchema>;

function RecomputePanel(): JSX.Element {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<Array<{ employeeId: string; date: string; reason: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<RecomputeValues>({
    resolver: zodResolver(RecomputeSchema),
    defaultValues: { periodStart: '', periodEnd: '' },
  });

  const mut = useMutation({
    mutationFn: (v: RecomputeValues) => aggregateTimesheetPeriods(v),
    onSuccess: async (res) => {
      setError(null);
      setResult(`Recomputed ${res.periods.length} period(s).`);
      setWarnings(res.warnings);
      await queryClient.invalidateQueries({ queryKey: ['attendance', 'timesheet-periods'] });
    },
    onError: (err: unknown) => {
      setResult(null);
      setWarnings([]);
      setError(err instanceof ApiError ? err.message : 'Recompute failed.');
    },
  });

  return (
    <form onSubmit={handleSubmit((v) => mut.mutate(v))} className="rounded border border-gray-900 p-4 space-y-3 max-w-xl" noValidate>
      <h2 className="text-sm uppercase tracking-wide text-gray-400">Recompute a period</h2>
      {result ? <div className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{result}</div> : null}
      <div className="flex gap-3">
        <label className="block text-xs uppercase text-gray-400 flex-1">
          Period start
          <input type="date" {...register('periodStart')} className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
          {errors.periodStart ? <span className="text-xs text-red-400">{errors.periodStart.message}</span> : null}
        </label>
        <label className="block text-xs uppercase text-gray-400 flex-1">
          Period end
          <input type="date" {...register('periodEnd')} className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
          {errors.periodEnd ? <span className="text-xs text-red-400">{errors.periodEnd.message}</span> : null}
        </label>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {warnings.length > 0 ? (
        <div className="rounded border border-yellow-900 bg-yellow-950/30 p-3 text-xs text-yellow-300 space-y-1">
          <p className="uppercase tracking-wide text-yellow-400">{warnings.length} dangling-punch warning(s)</p>
          <ul className="max-h-32 overflow-y-auto space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}><span className="font-mono text-yellow-500">{w.date}</span> · {w.reason}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <button type="submit" disabled={isSubmitting || mut.isPending} className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50">
        {mut.isPending ? 'Recomputing…' : 'Recompute'}
      </button>
    </form>
  );
}

function PeriodsTable(): JSX.Element {
  const periodsQuery = useQuery({ queryKey: ['attendance', 'timesheet-periods'], queryFn: () => listTimesheetPeriods() });
  // Employee names client-side (the timesheet endpoint returns ids only).
  const employeesQuery = useQuery({ queryKey: ['employees', 'picker'], queryFn: () => listEmployees({ status: 'active', limit: 200 }) });
  const nameById = new Map((employeesQuery.data?.employees ?? []).map((e) => [e.id, `${e.firstName} ${e.lastName}`]));

  if (periodsQuery.isLoading) return <Placeholder>Loading…</Placeholder>;
  if (periodsQuery.isError) return <Placeholder error>Could not load timesheets.</Placeholder>;
  const periods = periodsQuery.data?.periods ?? [];
  if (periods.length === 0) return <Placeholder>No timesheet periods yet. Recompute a period above.</Placeholder>;

  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Employee</th>
            <th className="text-left px-4 py-2">Period</th>
            <th className="text-left px-4 py-2">Mode</th>
            <th className="text-right px-4 py-2">Regular</th>
            <th className="text-right px-4 py-2">Overtime</th>
            <th className="text-right px-4 py-2">Absence</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p.id} className="border-t border-gray-900">
              <td className="px-4 py-3 text-gray-300">{nameById.get(p.employeeId) ?? <span className="font-mono text-xs text-gray-500">{p.employeeId}</span>}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-300">{formatDate(p.periodStart)} → {formatDate(p.periodEnd)}</td>
              <td className="px-4 py-3 text-gray-400">{p.operatingMode}</td>
              <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.regularHours)}</td>
              <td className="px-4 py-3 text-right font-mono text-gray-300">{formatNumber(p.overtimeHours)}</td>
              <td className="px-4 py-3 text-right font-mono text-gray-400">{formatNumber(p.absenceDays)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
